// utils/uas.js
// Shared plumbing for the UAS module.
//
// The legacy UAS pages repeated the same request boilerplate ~25 times, wrote
// the ticks↔date conversion three times (two of them UTC-naive while a third
// applied the user's offset, so grids and detail panes disagreed), and
// hand-cloned DTOs property-by-property on edit — the Incidents page dropped
// twelve fields that way. All of that lives here once.

import { readApiJson } from '@/utils/apiHelpers';

export const UAS_API = '/api/portal/uas';
export const UAS_AUTH_API = '/api/auth';
export const UAS_APPLET_ID = 'uas';

// Legacy `isUasSpecialUser` — note this is a SHORTER list than the portal-wide
// super-admin set; only these two see the UAS operational pages.
export const UAS_SPECIAL_USER_IDS = [
    '5555555@internal.fire.lacounty.gov',
    '2222222@internal.fire.lacounty.gov',
];

export function isUasSpecialUser(user) {
    return !!user?.id && UAS_SPECIAL_USER_IDS.includes(user.id);
}

// ── .NET ticks ⇄ JS date ─────────────────────────────────────────────────────
const EPOCH_TICKS = 621355968000000000;

export function ticksToDate(ticks) {
    const n = Number(ticks);
    if (!n || Number.isNaN(n)) return null;
    const d = new Date((n - EPOCH_TICKS) / 10000);
    return Number.isNaN(d.getTime()) || d.getFullYear() <= 1 ? null : d;
}

export function dateToTicks(date) {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    return Number.isNaN(d.getTime()) ? null : d.getTime() * 10000 + EPOCH_TICKS;
}

const pad = n => String(n).padStart(2, '0');

/** yyyy-MM-dd for <input type="date">.
 *  Date-only ticks are calendar dates at midnight with no timezone (legacy
 *  binds them straight to DateTime), so read them back with UTC getters —
 *  local getters shift the date one day early for anyone west of UTC. */
export function ticksToDateInput(ticks) {
    const d = ticksToDate(ticks);
    return d ? `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` : '';
}

export function dateInputToTicks(value) {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    // Date.UTC keeps the picked calendar date as-is, matching the ticks the
    // legacy portal writes (calendar midnight, not local midnight).
    return y ? Date.UTC(y, (m || 1) - 1, d || 1) * 10000 + EPOCH_TICKS : null;
}

/** yyyy-MM-ddTHH:mm for <input type="datetime-local">. */
export function ticksToDateTimeInput(ticks) {
    const d = ticksToDate(ticks);
    return d
        ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
        : '';
}

export function dateTimeInputToTicks(value) {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : dateToTicks(d);
}

export function formatDate(ticks) {
    const d = ticksToDate(ticks);
    if (!d) return '—';
    // Legacy formats date-only ticks as MM/dd/yyyy from the raw calendar date;
    // UTC getters keep the same date in every timezone.
    return `${pad(d.getUTCMonth() + 1)}/${pad(d.getUTCDate())}/${d.getUTCFullYear()}`;
}

export function formatDateTime(ticks) {
    const d = ticksToDate(ticks);
    if (!d) return '—';
    // Legacy format: MM/dd/yyyy HH:mm (24-hour, no seconds). Local getters —
    // timestamps (unlike date-only ticks) are real instants.
    return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Whole days from now until the given ticks; negative when past.
 *  Truncates toward zero like legacy's (int)TotalDays cast — Math.ceil showed
 *  one more day than the legacy dashboards for future dates. */
export function daysUntil(ticks) {
    const d = ticksToDate(ticks);
    if (!d) return null;
    return Math.trunc((d.getTime() - Date.now()) / 86400000);
}

export function formatDaysRemaining(ticks) {
    const days = daysUntil(ticks);
    if (days === null) return '—';
    return days < 0 ? `Expired ${Math.abs(days)}d ago` : `${days}d remaining`;
}

// ── Requests ─────────────────────────────────────────────────────────────────
/**
 * POST a UAS endpoint. `Key`/`OrgId` are stamped automatically.
 * Returns the normalized (camelCase) response body.
 */
export async function uasPost(endpoint, { authKey, orgId, ...body } = {}) {
    const res = await fetch(`${UAS_API}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Key: authKey, OrgId: orgId, ...body }),
    });
    if (!res.ok) throw new Error(`Request failed (${res.status})`);
    return readApiJson(await res.json());
}

/** Deep clone so edit forms can't silently drop fields they don't render. */
export function cloneDto(src) {
    return src ? JSON.parse(JSON.stringify(src)) : null;
}

// ── Sorting ──────────────────────────────────────────────────────────────────
export function nextSort(current, column, defaultDescending = false) {
    if (current.column === column) {
        return { column, ascending: !current.ascending };
    }
    return { column, ascending: !defaultDescending };
}

export function sortRows(rows, { column, ascending }, accessors = {}) {
    if (!column) return rows;
    const get = accessors[column] || (r => r[column]);
    return rows.slice().sort((a, b) => {
        const av = get(a), bv = get(b);
        // Nulls sort last in both directions, matching the legacy pages
        // (?? long.MaxValue ascending, ?? 0 descending).
        if (av === null || av === undefined) return (bv === null || bv === undefined) ? 0 : 1;
        if (bv === null || bv === undefined) return -1;
        if (typeof av === 'number' && typeof bv === 'number') {
            return ascending ? av - bv : bv - av;
        }
        const cmp = String(av).localeCompare(String(bv));
        return ascending ? cmp : -cmp;
    });
}

// ── Display maps ─────────────────────────────────────────────────────────────
export const SEVERITY_ORDER = { Critical: 0, Major: 1, Minor: 2 };
export const DRONE_INCIDENT_STATUS_ORDER = { Open: 0, InProgress: 1, Closed: 2 };

export const STATUS_LABELS = {
    Open: 'Open', InProgress: 'In Progress', Closed: 'Closed',
    Current: 'Current', ExpiringSoon: 'Expiring Soon', Expired: 'Expired',
};

export function statusLabel(value) {
    if (!value) return '—';
    return STATUS_LABELS[value] || String(value).replace(/([a-z])([A-Z])/g, '$1 $2');
}

export const INCIDENT_TYPES = [
    'Wildland', 'Prescribed Fire', 'Mud and Debris Flow', 'Commercial Fire', 'Other',
];

// Per-type badge color + blob icon, matching the legacy incidents table
// (Incidents.razor.css .type-* classes and GetIncidentIconUrl).
const INCIDENT_ICON_BASE = 'https://fireops.blob.core.windows.net/icons/global/';
const INCIDENT_TYPE_CONFIG = {
    'Wildland': { badge: 'badge-orange', iconUrl: `${INCIDENT_ICON_BASE}incident_wildland.png` },
    'Prescribed Fire': { badge: 'badge-green', iconUrl: `${INCIDENT_ICON_BASE}incident_wildland.png` },
    'Mud and Debris Flow': { badge: 'badge-brown', iconUrl: `${INCIDENT_ICON_BASE}incident_mud_debris.png` },
    'Commercial Fire': { badge: 'badge-red', iconUrl: `${INCIDENT_ICON_BASE}incident_commercial.png` },
    'Other': { badge: 'badge-slate', iconUrl: null },
};
export const incidentTypeInfo = type =>
    INCIDENT_TYPE_CONFIG[type] || { badge: 'badge-gray', iconUrl: null };

export const SAFETY_TYPES = ['Incident', 'Accident', 'Hazard'];
export const SEVERITIES = ['Minor', 'Major', 'Critical'];
export const SAFETY_STATUSES = ['Open', 'InProgress', 'Closed'];
export const CURRENCY_STATUSES = ['Current', 'ExpiringSoon', 'Expired'];

// ── Drones ───────────────────────────────────────────────────────────────────
export const DRONE_STATUSES = ['Airworthy', 'InMaintenance', 'Grounded', 'Retired'];

/** Mirrors the server-side transition matrix. A new drone cannot start Retired. */
export const DRONE_STATUS_TRANSITIONS = {
    Airworthy: ['InMaintenance', 'Grounded', 'Retired'],
    InMaintenance: ['Airworthy', 'Grounded', 'Retired'],
    Grounded: ['InMaintenance', 'Airworthy', 'Retired'],
    Retired: ['Grounded'],
};

export function allowedTransitions(status) {
    return DRONE_STATUS_TRANSITIONS[status] || [];
}

/** Current status first, then everything reachable from it. */
export function editableStatuses(status) {
    return [status, ...allowedTransitions(status).filter(s => s !== status)];
}

export const PAYLOAD_TYPES = ['IRCamera', 'RGBCamera', 'LiDAR', 'Multispectral', 'Thermal', 'Other'];

const PAYLOAD_TYPE_LABELS = { IRCamera: 'IR Camera', RGBCamera: 'RGB Camera' };
export const payloadTypeLabel = t => PAYLOAD_TYPE_LABELS[t] || t || '—';

const PAYLOAD_STATUS_LABELS = { InUse: 'In Use', InMaintenance: 'Maintenance' };
export const payloadStatusLabel = s => PAYLOAD_STATUS_LABELS[s] || s || '—';

// ── Missions ─────────────────────────────────────────────────────────────────
export const MISSION_TYPES = [
    'HotspotDetection', 'Reconnaissance', 'Inspection', 'Surveillance',
    'Mapping', 'Training', 'DamageAssessment', 'Other',
];
export const MISSION_STATUSES = ['Planned', 'InProgress', 'Complete', 'UnderReview'];
export const MEDIA_TYPES = ['Photo', 'Video', 'Document', 'Orthomosaic', 'ThermalMap', 'LiDAR', '3DModel'];
export const DATA_PRODUCT_TYPES = ['Orthomosaic', 'ThermalMap', 'Video', 'Photo', 'LiDAR', '3DModel'];

const MISSION_TYPE_LABELS = {
    HotspotDetection: 'Hotspot Detection',
    DamageAssessment: 'Damage Assessment',
};
export const missionTypeLabel = t => MISSION_TYPE_LABELS[t] || t || '—';

const MEDIA_TYPE_LABELS = { ThermalMap: 'Thermal Map', '3DModel': '3D Model' };
export const mediaTypeLabel = t => MEDIA_TYPE_LABELS[t] || t || '—';

export function formatDuration(minutes) {
    const m = Number(minutes);
    if (!m || Number.isNaN(m)) return '—';
    const h = Math.floor(m / 60);
    return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
}

export function formatCoords(lat, lon) {
    if (lat == null || lon == null) return '—';
    return `${Number(lat).toFixed(5)}, ${Number(lon).toFixed(5)}`;
}

export function initials(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Parse a JSON array field, returning [] on anything unparseable. */
export function parseJsonArray(json) {
    if (!json) return [];
    try {
        const v = JSON.parse(json);
        return Array.isArray(v) ? v : [];
    } catch {
        return [];
    }
}

// Badge colour classes, keyed by the raw wire value.
export function badgeClass(kind, value) {
    const kindV = String(value || '').toLowerCase();
    // Colors mirror the legacy Missions.razor.css palette.
    if (kind === 'missionStatus') {
        return kindV === 'complete' ? 'badge-green'
            : kindV === 'inprogress' ? 'badge-orange'
            : kindV === 'underreview' ? 'badge-purple'
            : kindV === 'planned' ? 'badge-blue'
            : 'badge-gray';
    }
    if (kind === 'missionType') {
        return kindV === 'hotspotdetection' ? 'badge-red'
            : kindV === 'reconnaissance' ? 'badge-blue'
            : kindV === 'inspection' ? 'badge-teal'
            : kindV === 'surveillance' ? 'badge-indigo'
            : kindV === 'mapping' ? 'badge-green'
            : kindV === 'training' || kindV === 'damageassessment' ? 'badge-orange'
            : 'badge-slate';
    }
    if (kind === 'mediaType') {
        return kindV === 'photo' ? 'badge-blue'
            : kindV === 'video' ? 'badge-purple'
            : kindV === 'thermalmap' ? 'badge-orange'
            : kindV === 'orthomosaic' ? 'badge-green'
            : kindV === 'lidar' ? 'badge-yellow'
            : 'badge-gray';
    }
    // Colors mirror the legacy Drones.razor.css badge palette.
    if (kind === 'droneStatus') {
        return kindV === 'airworthy' ? 'badge-green'
            : kindV === 'inmaintenance' ? 'badge-orange'
            : kindV === 'grounded' ? 'badge-red'
            : 'badge-slate';
    }
    if (kind === 'payloadStatus') {
        return kindV === 'available' ? 'badge-green'
            : kindV === 'inuse' ? 'badge-orange'
            : kindV === 'inmaintenance' ? 'badge-red'
            : 'badge-slate';
    }
    if (kind === 'payloadType') {
        return kindV === 'ircamera' ? 'badge-red'
            : kindV === 'thermal' ? 'badge-orange'
            : kindV === 'rgbcamera' ? 'badge-blue'
            : kindV === 'lidar' ? 'badge-green'
            : kindV === 'multispectral' ? 'badge-yellow'
            : 'badge-slate';
    }
    if (kind === 'alertGrade') {
        return kindV === 'critical' ? 'badge-red' : kindV === 'warning' ? 'badge-yellow' : 'badge-blue';
    }
    if (kind === 'checklistResult') {
        return kindV === 'pass' ? 'badge-green' : kindV === 'fail' ? 'badge-red' : 'badge-orange';
    }
    return badgeClassBase(kind, value);
}

function badgeClassBase(kind, value) {
    const v = String(value || '').toLowerCase();
    if (kind === 'severity') {
        return v === 'critical' ? 'badge-red' : v === 'major' ? 'badge-orange' : 'badge-yellow';
    }
    if (kind === 'safetyStatus') {
        // Legacy .status-open is orange (#FFF3E0/#E65100), not yellow.
        return v === 'closed' ? 'badge-green' : v === 'inprogress' ? 'badge-blue' : 'badge-orange';
    }
    if (kind === 'currency') {
        // Legacy .badge-expiring is orange (#FFF3E0/#E65100), not yellow.
        return v === 'current' ? 'badge-green' : v === 'expiringsoon' ? 'badge-orange' : 'badge-red';
    }
    if (kind === 'safetyType') {
        return v === 'accident' ? 'badge-red' : v === 'hazard' ? 'badge-orange' : 'badge-blue';
    }
    return 'badge-gray';
}

// ── Aerial jump-off (legacy HotspotImport.GoToAerialImages, 07/2026) ─────────
// The import result modal hands the just-published incident to the Hotspots
// page, which drills into it and opens the aerial manager. Legacy rides its
// ImportResultSummary temp slot with OpenAerialForIncidentId set; here it is
// sessionStorage, take-once, the same mechanism as the Nova handoff.
const AERIAL_HANDOFF_KEY = 'uas.openAerialForIncidentId';

export function setAerialHandoff(incidentId) {
    if (!incidentId) return;
    try { sessionStorage.setItem(AERIAL_HANDOFF_KEY, String(incidentId)); }
    catch { /* private mode — the Hotspots page simply opens normally */ }
}

/** Returns the incident id once and clears it; null when nothing was handed off. */
export function takeAerialHandoff() {
    try {
        const id = sessionStorage.getItem(AERIAL_HANDOFF_KEY);
        sessionStorage.removeItem(AERIAL_HANDOFF_KEY);
        return id || null;
    } catch { return null; }
}
